"use client";

import { addToCart } from "@lib/data/cart";
import { useCart } from "@context/cart-context";
import { useAnalytics } from "@context/analytics-context";
import { mapProductToItem } from "@lib/analytics/mappers";
import { useIntersection } from "@lib/hooks/use-in-view";
import { Button, Text } from "@medusajs/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import ProductPrice from "../product-price";
import OptionSelect from "./option-select";
import Input from "@modules/common/components/input";
import { getVariantIdentifier } from "@lib/util/variants";
import { useCartDrawer } from "@context/cart-drawer-context";
import { getVariantLabel } from "@modules/products/components/product-preview/quick-add-drawer";
import { getSearchAttribution } from "@lib/search/attribution";
import MobileActions from "./mobile-actions";
import { resolveInventoryAvailability } from "@modules/products/components/product-preview/stock";
type ProductActionsProps = {
  product: any;
  region: any;
  disabled?: boolean;
};
export default function ProductActions({
  product,
  disabled,
  region
}: ProductActionsProps) {
  const [isAdding, setIsAdding] = useState(false);
  const productOptions = useMemo(() => Array.isArray(product?.options) ? product.options : [], [product?.options]);
  const productVariants = useMemo(() => Array.isArray(product?.variants) ? product.variants : [], [product?.variants]);
  const defaultVariant = useMemo(() => {
    if (!productVariants.length) return undefined;
    return productVariants.find((variant: any) => variant?.isDefault) || productVariants[0];
  }, [productVariants]);
  const initialOptions = useMemo(() => {
    if (!productOptions.length) {
      return {};
    }
    const normalized: Record<string, string> = {};
    if (defaultVariant) {
      const variantOptionValues: Record<string, string> | undefined = defaultVariant?.optionValues && typeof defaultVariant.optionValues === "object" ? defaultVariant.optionValues : Array.isArray(defaultVariant?.options) ? defaultVariant.options.reduce((acc: Record<string, string>, opt: any) => {
        const optName = opt?.option_name || opt?.name || opt?.title || opt?.id || opt?._id;
        const optValue = opt?.option_value || opt?.value;
        if (typeof optName === "string" && optName.length > 0 && typeof optValue === "string" && optValue.length > 0) {
          acc[optName] = optValue;
        }
        return acc;
      }, {} as Record<string, string>) : undefined;
      if (variantOptionValues) {
        for (const option of productOptions) {
          const name = option?.name;
          if (typeof name !== "string" || !name.length) continue;
          const candidateValue = variantOptionValues[name] ?? variantOptionValues[option?.title ?? ""] ?? variantOptionValues[String(option?.id ?? "")] ?? variantOptionValues[String(option?._id ?? "")];
          if (typeof candidateValue === "string" && candidateValue.length > 0) {
            normalized[name] = candidateValue;
          }
        }
      }
    }
    if (!Object.keys(normalized).length) {
      for (const option_0 of productOptions) {
        const name_0 = option_0?.name;
        if (typeof name_0 !== "string" || !name_0.length) continue;
        const values = (option_0?.values ?? []).map((v: any) => typeof v === "string" ? v : v?.value).filter((val: any): val is string => typeof val === "string" && val.length > 0);
        if (values.length === 1) {
          normalized[name_0] = values[0];
        }
      }
    }
    return normalized;
  }, [defaultVariant, productOptions]);
  const [options, setOptions] = useState<Record<string, string>>(initialOptions);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setOptions(initialOptions);
    setError(null);
  }, [initialOptions]);
  const [quantity, setQuantity] = useState(1);
  const {
    setCart,
    refreshCart
  } = useCart();
  const analytics = useAnalytics();
  const {
    open: openCartDrawer
  } = useCartDrawer();
  const resolvedSearchAttribution = useMemo(() => {
    const productId = String(product?._id || product?.id || "");
    return getSearchAttribution(productId || undefined, product?.slug);
  }, [product?._id, product?.id, product?.slug]);
  const getOptionKeyCandidates = (option_1: any) => [option_1?.name, option_1?.title, option_1?.id ? String(option_1.id) : undefined, option_1?._id ? String(option_1._id) : undefined].filter((val_0): val_0 is string => typeof val_0 === "string" && val_0.length > 0);
  const updateOption = (id: string, value: string) => {
    // Support identifying option by id/_id or by name/title
    const opt_0 = (productOptions || []).find((o: any) => {
      const oid = String(o.id || o._id);
      return oid === id || o.name === id || o.title === id;
    });
    if (opt_0) {
      const candidates = getOptionKeyCandidates(opt_0);
      if (!candidates.length) return;
      setOptions(prev => {
        const next = {
          ...prev
        };
        for (const key of candidates) {
          next[key] = value;
        }
        return next;
      });
    } else {
      // Fallback: use provided id as the name key
      setOptions(prev_0 => ({
        ...prev_0,
        [id]: value
      }));
    }
  };
  const allOptionsSelected = productOptions.length ? productOptions.every((opt_1: any) => {
    const candidates_0 = getOptionKeyCandidates(opt_1);
    if (!candidates_0.length) {
      return true;
    }
    return candidates_0.some(key_0 => {
      const value_0 = options[key_0];
      return typeof value_0 === "string" && value_0.length > 0;
    });
  }) : true;
  const doesVariantMatchSelection = (variant_0: any, selection: Record<string, string>) => {
    if (!variant_0) return false;
    const variantValues: Record<string, string> | undefined = variant_0?.optionValues && typeof variant_0.optionValues === "object" ? variant_0.optionValues : undefined;
    return productOptions.every((opt_2: any) => {
      const candidates_1 = getOptionKeyCandidates(opt_2);
      if (!candidates_1.length) {
        return true;
      }
      const selectedValue = candidates_1.map(key_1 => selection[key_1]).find((val_1): val_1 is string => typeof val_1 === "string" && val_1.length > 0);
      if (!selectedValue) {
        return false;
      }
      const valueFromVariant = variantValues ? candidates_1.map(key_2 => variantValues[key_2]).find((val_2): val_2 is string => typeof val_2 === "string" && val_2.length > 0) : undefined;
      if (typeof valueFromVariant === "string" && valueFromVariant.length) {
        return valueFromVariant === selectedValue;
      }
      if (Array.isArray(variant_0?.options)) {
        return variant_0.options.some((variantOption: any) => {
          const variantOptionName = variantOption?.option_name || variantOption?.name || variantOption?.title || variantOption?.option_id || variantOption?.id || variantOption?._id;
          const variantOptionValue = variantOption?.option_value || variantOption?.value;
          if (typeof variantOptionValue !== "string" || !variantOptionValue.length) {
            return false;
          }
          if (!variantOptionName) {
            return variantOptionValue === selectedValue;
          }
          return candidates_1.some(candidate => String(variantOptionName).toLowerCase() === candidate.toLowerCase()) && variantOptionValue === selectedValue;
        });
      }
      return false;
    });
  };
  const findVariant = () => {
    if (!allOptionsSelected) return null;
    return productVariants.find((variant_1: any) => doesVariantMatchSelection(variant_1, options)) || null;
  };
  const selectedVariant = findVariant();
  useEffect(() => {
    if (error && allOptionsSelected) {
      setError(null);
    }
  }, [allOptionsSelected, error]);
  let track: boolean | undefined;
  let inStock: boolean;
  let effectiveStock: number | undefined;
  if (Array.isArray(product.options) && product.options.length > 0) {
    track = selectedVariant ? typeof selectedVariant.trackInventory === "boolean" ? selectedVariant.trackInventory : product.trackInventory : product.trackInventory;
    if (selectedVariant) {
      const availability = resolveInventoryAvailability({
        trackInventory: track,
        stock: selectedVariant?.stock ?? selectedVariant?.inventory_quantity,
        inventoryLevels: selectedVariant?.inventoryLevels
      });
      effectiveStock = typeof availability.quantity === "number" ? availability.quantity : undefined;
      inStock = availability.inStock;
    } else {
      inStock = false;
      effectiveStock = undefined;
    }
  } else {
    const availability_0 = resolveInventoryAvailability({
      trackInventory: product?.trackInventory,
      stock: product?.stock,
      inventoryLevels: product?.inventoryLevels,
      variants: product?.variants
    });
    track = availability_0.tracked;
    effectiveStock = typeof availability_0.quantity === "number" ? availability_0.quantity : undefined;
    inStock = availability_0.inStock;
  }
  const showLowStockMessage = typeof effectiveStock === "number" && effectiveStock === 1 && track !== false;
  const getDisabledValues = (option_2: any) => {
    const rawValues = option_2.values || [];
    const values_0: string[] = rawValues.map((v_0: any) => typeof v_0 === "string" ? v_0 : v_0?.value).filter(Boolean);
    return values_0.filter((val_3: string) => {
      const candidates_2 = getOptionKeyCandidates(option_2);
      if (!candidates_2.length) {
        return false;
      }
      const testSelection = {
        ...options
      };
      for (const key_3 of candidates_2) {
        testSelection[key_3] = val_3;
      }
      const variant_2 = productVariants.find((vr: any) => doesVariantMatchSelection(vr, testSelection));
      if (!variant_2) {
        return productVariants.length > 0;
      }
      const tr = typeof variant_2.trackInventory === "boolean" ? variant_2.trackInventory : product.trackInventory;
      const availability_1 = resolveInventoryAvailability({
        trackInventory: tr,
        stock: variant_2?.stock ?? variant_2?.inventory_quantity,
        inventoryLevels: variant_2?.inventoryLevels
      });
      return !availability_1.inStock;
    });
  };
  const actionsRef = useRef<HTMLDivElement>(null);
  const inView = useIntersection(actionsRef, "0px");
  const handleAddToCart = async () => {
    const slug = product.slug;
    if (!slug) return null;
    const productId_0 = String(product?._id || product?.id || "").trim();
    if (!allOptionsSelected) {
      setError("Por favor selecciona todas las opciones antes de agregar al carrito.");
      return null;
    }
    if (productOptions.length > 0 && !selectedVariant) {
      setError("La combinación seleccionada no está disponible.");
      return null;
    }
    if (!inStock) {
      setError("Esta variante no tiene stock disponible en este momento.");
      return null;
    }
    setError(null);
    setIsAdding(true);
    try {
      const optionPayload = productOptions.reduce((acc_0: Record<string, string>, option_3: any) => {
        const candidates_3 = getOptionKeyCandidates(option_3);
        if (!candidates_3.length) {
          return acc_0;
        }
        const selectedValue_0 = candidates_3.map(key_4 => options[key_4]).find((val_4): val_4 is string => typeof val_4 === "string" && val_4.length > 0);
        if (!selectedValue_0) {
          return acc_0;
        }
        for (const key_5 of candidates_3) {
          acc_0[key_5] = selectedValue_0;
        }
        return acc_0;
      }, {} as Record<string, string>);
      const updatedCart = await addToCart({
        productId: productId_0 || undefined,
        productSlug: slug,
        quantity: quantity,
        variantOptionValues: productOptions.length ? optionPayload : undefined,
        variantId: selectedVariant ? getVariantIdentifier(selectedVariant) : undefined,
        searchAttribution: resolvedSearchAttribution ? {
          ...resolvedSearchAttribution,
          productId: resolvedSearchAttribution.productId || String(product?._id || product?.id || ""),
          platform: resolvedSearchAttribution.platform || "web"
        } : undefined
      });
      if (updatedCart) {
        setCart(updatedCart);
      } else {
        await refreshCart();
      }
      const {
        item,
        currency,
        unitPrice
      } = mapProductToItem(product, {
        quantity,
        variant: selectedVariant || undefined,
        fallbackCurrency: product?.currency || region?.currency_code
      });
      analytics.trackAddToCart({
        items: [item],
        currency,
        value: typeof unitPrice === "number" ? unitPrice * quantity : undefined
      });
      const variantForDrawer = selectedVariant || (!productOptions.length ? defaultVariant : undefined);
      openCartDrawer({
        title: product?.name,
        image: product?.thumbnail,
        variantLabel: variantForDrawer ? getVariantLabel(product, variantForDrawer) : undefined,
        quantity
      });
    } catch (error_0) {
      console.error("Failed to add item to cart", error_0);
      await refreshCart();
    } finally {
      setIsAdding(false);
    }
  };
  return <>
      <div className="flex flex-col gap-4" ref={actionsRef}>
        {/* Price */}
        <div className="">
          <ProductPrice product={product} />
          {showLowStockMessage ? <p className="mt-2 text-sm font-lealti-light text-black bg-yellow-100 rounded-lg px-2 py-1 w-fit shadow-2xl">
              Última unidad disponible
            </p> : null}
        </div>

        {/* Product Options */}
        {productOptions.map((opt_3: any) => {
        const candidates_4 = getOptionKeyCandidates(opt_3);
        const currentValue = candidates_4.map(key_6 => options[key_6]).find((val_5): val_5 is string => typeof val_5 === "string" && val_5.length > 0);
        const optionTitle = opt_3?.name || opt_3?.title || "Opción";
        return <div key={String(opt_3.id || opt_3._id || opt_3.name || opt_3.title)} className="space-y-3">
              <OptionSelect option={opt_3} current={currentValue} updateOption={updateOption} title={optionTitle} disabled={!!disabled || isAdding} disabledValues={getDisabledValues(opt_3)} data-testid="product-option" />
            </div>;
      })}

        {error ? <Text className="text-sm text-red-600">{error}</Text> : null}

        {/* Quantity Selector */}
        {/* <div className="space-y-3">
         <label className="text-sm font-medium text-black">
          Cantidad
         </label>
         <div className="flex items-center border-b border-black rounded-none w-32">
          <button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-black transition-colors"
            disabled={quantity <= 1}
          >
            -
          </button>
          <input
            name="quantity"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            className="flex-1 text-center border-none outline-none text-black shadow-none bg-transparent w-full "
            min="1"
          />
          <button
            onClick={() => setQuantity(quantity + 1)}
            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-black transition-colors"
          >
            +
          </button>
         </div>
         </div> */}

        {/* Action Buttons — desktop only; on mobile the fixed floating bar handles this */}
        <div className="hidden lg:flex gap-3">
          {/* Wishlist Button */}
          <button className="w-12 h-12 rounded-none flex items-center justify-center hover:border-gray-400 transition-colors">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>

          {/* Add to Cart Button */}
          <Button onClick={handleAddToCart} disabled={!!disabled || isAdding || !inStock || !allOptionsSelected} variant="primary" className="flex-1 h-12 bg-black text-white hover:bg-gray-800 transition-colors rounded-3xl font-lealti-light" isLoading={isAdding} data-testid="add-product-button">
            Agregar al carrito
          </Button>
        </div>
      </div>
      <MobileActions product={product} options={options} updateOption={updateOption} inStock={inStock} handleAddToCart={handleAddToCart} isAdding={isAdding} show={true} disabled={!!disabled} allOptionsSelected={allOptionsSelected} disabledValues={getDisabledValues} />
    </>;
}